import tokens from '@lessonnine/design-tokens.lib/dist/json/variables.json';
import { BabbelTheme } from '@lessonnine/design-tokens.lib/dist/json/theme';
import { fallbackTheme } from '../theming/fallbackTheme';
import { ShortTextProps, ShortTextSize } from '../Typography';
import { IconSize, InternalIconProps } from '../Icon/GenericIcon';
import { SpinnerProps, SpinnerSize } from '../Spinner';
import { ResponsiveProp } from '../utils/responsiveProp';
import {
  ButtonColor,
  ButtonColorLegacy,
  ButtonColorNew,
  ButtonWithoutTextSize,
  ButtonSizeLegacy,
  ButtonSize,
} from './types';

/**
 * Get non-legacy `color` value.
 *
 * `Button` supports some legacy `color` values which are deprecated but many clients likely still use.
 * If the given `color` is already one of the new values, return it as is.
 * If the given `color` is one of the legacy values, map it to the new equivalent and return that value.
 *
 * TODO: remove once all clients finished migrating to the new values or we created a codemod.
 */
export const getColorWithLegacyValuesMapped = (color: ButtonColor): ButtonColorNew => {
  const colorLegacyToNewMapping: Record<ButtonColorLegacy, ButtonColorNew> = {
    primary: 'primary-emphasis',
    secondary: 'secondary-default',
    tertiary: 'ghost',
    inverse: 'secondary-inverse',
  };
  let buttonColor;

  if (Object.keys(colorLegacyToNewMapping).includes(color)) {
    // type-casting to let TS know we're certain this is a legacy color value
    const legacyColor = color as ButtonColorLegacy;
    buttonColor = colorLegacyToNewMapping[legacyColor];
  } else {
    // type-casting to let TS know we're certain this is a new color value
    const newColor = color as ButtonColorNew;
    buttonColor = newColor;
  }

  return buttonColor;
};

/**
 * Get color-related tokens based on the given `color` prop
 */
export const getColorTokens = (color: ButtonColor, theme: BabbelTheme) => {
  const { color: colors } = fallbackTheme(theme);

  switch (color) {
    case 'secondary-default':
      return {
        spinner: 'default',
        default: {
          background: colors.accentInverse.enabled,
          foreground: colors.accentInverse.onEnabled,
          border: colors.accentInverse.onEnabled,
        },
        hover: {
          background: colors.accentInverse.hover,
          foreground: colors.accentInverse.onEnabled,
          border: colors.accentInverse.onEnabled,
        },
        active: {
          background: colors.accentInverse.active,
          foreground: colors.accentInverse.onEnabled,
          border: colors.accentInverse.onEnabled,
        },
        disabled: {
          background: colors.other.none,
          foreground: colors.disabled.onSurfaceSubdued,
          border: colors.disabled.onSurfaceSubdued,
        },
      };
    case 'primary-emphasis':
      return {
        spinner: 'inverse',
        default: {
          background: colors.brandInverse.onEnabled,
          foreground: colors.brand.onEnabled,
          border: colors.brandInverse.onEnabled,
        },
        hover: {
          background: colors.brand.hover,
          foreground: colors.brand.onEnabled,
          border: colors.brand.hover,
        },
        active: {
          background: colors.brand.active,
          foreground: colors.brand.onEnabled,
          border: colors.brand.active,
        },
        disabled: {
          background: colors.disabled.default,
          foreground: colors.disabled.onSurfaceDefault,
          border: colors.disabled.surface,
        },
      };
    case 'secondary-emphasis':
      return {
        spinner: 'default',
        default: {
          background: colors.brandInverse.enabled,
          foreground: colors.brandInverse.onEnabled,
          border: colors.brandInverse.onEnabled,
        },
        hover: {
          background: colors.brandInverse.hover,
          foreground: colors.brandInverse.onEnabled,
          border: colors.brandInverse.onEnabled,
        },
        active: {
          background: colors.brandInverse.active,
          foreground: colors.brandInverse.onEnabled,
          border: colors.brandInverse.onEnabled,
        },
        disabled: {
          background: colors.other.none,
          foreground: colors.disabled.onSurfaceSubdued,
          border: colors.disabled.onSurfaceSubdued,
        },
      };
    case 'ghost':
      return {
        spinner: 'default',
        default: {
          background: colors.other.none,
          foreground: colors.accentInverse.onEnabled,
          border: colors.other.none,
        },
        hover: {
          background: colors.accentInverse.hover,
          foreground: colors.accentInverse.onEnabled,
          border: colors.accentInverse.hover,
        },
        active: {
          background: colors.accentInverse.active,
          foreground: colors.accentInverse.onEnabled,
          border: colors.accentInverse.active,
        },
        disabled: {
          background: colors.other.none,
          foreground: colors.disabled.onSurfaceSubdued,
          border: colors.other.none,
        },
      };
    case 'primary-inverse':
      return {
        spinner: 'default',
        default: {
          background: colors.accentInverse.enabled,
          foreground: colors.accentInverse.onEnabled,
          border: colors.accentInverse.enabled,
        },
        hover: {
          background: colors.accentInverse.hover,
          foreground: colors.accentInverse.onEnabled,
          border: colors.accentInverse.hover,
        },
        active: {
          background: colors.accentInverse.active,
          foreground: colors.accentInverse.onEnabled,
          border: colors.accentInverse.active,
        },
        disabled: {
          background: colors.disabled.surfaceInverse,
          foreground: colors.disabled.onSurfaceInverse,
          border: colors.disabled.surfaceInverse,
        },
      };
    case 'secondary-inverse':
      return {
        spinner: 'inverse',
        default: {
          background: colors.accent.enabled,
          foreground: colors.accent.onEnabled,
          border: colors.accent.onEnabled,
        },
        hover: {
          background: colors.accent.hover,
          foreground: colors.accent.onEnabled,
          border: colors.accent.onEnabled,
        },
        active: {
          background: colors.accent.active,
          foreground: colors.accent.onEnabled,
          border: colors.accent.onEnabled,
        },
        disabled: {
          background: colors.other.none,
          foreground: colors.disabled.onSurfaceInverse,
          border: colors.disabled.onSurfaceInverse,
        },
      };
    case 'ghost-inverse':
      return {
        spinner: 'inverse',
        default: {
          background: colors.other.none,
          foreground: colors.accent.onEnabled,
          border: colors.other.none,
        },
        hover: {
          background: colors.accent.hover,
          foreground: colors.accent.onEnabled,
          border: colors.accent.hover,
        },
        active: {
          background: colors.accent.active,
          foreground: colors.accent.onEnabled,
          border: colors.accent.active,
        },
        disabled: {
          background: colors.other.none,
          foreground: colors.disabled.onSurfaceInverse,
          border: colors.other.none,
        },
      };
    case 'primary-on-color':
      return {
        spinner: 'inverse',
        default: {
          background: colors.hidden.onIllustrationDark100,
          foreground: colors.hidden.onIllustrationLight100,
          border: colors.other.none,
        },
        hover: {
          background: colors.hidden.onIllustrationDark80,
          foreground: colors.hidden.onIllustrationLight100,
          border: colors.other.none,
        },
        active: {
          background: colors.hidden.onIllustrationDark60,
          foreground: colors.hidden.onIllustrationLight100,
          border: colors.other.none,
        },
        disabled: {
          background: colors.hidden.onIllustrationDark30,
          foreground: colors.hidden.onIllustrationLight40,
          border: colors.other.none,
        },
      };
    case 'primary-on-color-inverse':
      return {
        spinner: 'default',
        default: {
          background: colors.hidden.onIllustrationLight100,
          foreground: colors.hidden.onIllustrationDark100,
          border: colors.other.none,
        },
        hover: {
          background: colors.hidden.onIllustrationLight70,
          foreground: colors.hidden.onIllustrationDark100,
          border: colors.other.none,
        },
        active: {
          background: colors.hidden.onIllustrationLight50,
          foreground: colors.hidden.onIllustrationDark100,
          border: colors.other.none,
        },
        disabled: {
          background: colors.hidden.onIllustrationLight30,
          foreground: colors.hidden.onIllustrationDark40,
          border: colors.other.none,
        },
      };
    case 'secondary-on-color':
      return {
        spinner: 'default',
        default: {
          background: colors.other.none,
          foreground: colors.hidden.onIllustrationDark100,
          border: colors.hidden.onIllustrationDark100,
        },
        hover: {
          background: colors.hidden.onIllustrationLight30,
          foreground: colors.hidden.onIllustrationDark100,
          border: colors.hidden.onIllustrationDark100,
        },
        active: {
          background: colors.hidden.onIllustrationLight50,
          foreground: colors.hidden.onIllustrationDark100,
          border: colors.hidden.onIllustrationDark100,
        },
        disabled: {
          background: colors.other.none,
          foreground: colors.hidden.onIllustrationDark30,
          border: colors.hidden.onIllustrationDark30,
        },
      };
    case 'secondary-on-color-inverse':
      return {
        spinner: 'inverse',
        default: {
          background: colors.other.none,
          foreground: colors.hidden.onIllustrationLight100,
          border: colors.hidden.onIllustrationLight100,
        },
        hover: {
          background: colors.hidden.onIllustrationLight20,
          foreground: colors.hidden.onIllustrationLight100,
          border: colors.hidden.onIllustrationLight100,
        },
        active: {
          background: colors.hidden.onIllustrationLight40,
          foreground: colors.hidden.onIllustrationLight100,
          border: colors.hidden.onIllustrationLight100,
        },
        disabled: {
          background: colors.other.none,
          foreground: colors.hidden.onIllustrationLight30,
          border: colors.hidden.onIllustrationLight40,
        },
      };
    case 'ghost-on-color':
      return {
        spinner: 'default',
        default: {
          background: colors.other.none,
          foreground: colors.hidden.onIllustrationDark100,
          border: colors.other.none,
        },
        hover: {
          background: colors.hidden.onIllustrationLight30,
          foreground: colors.hidden.onIllustrationDark100,
          border: colors.other.none,
        },
        active: {
          background: colors.hidden.onIllustrationLight50,
          foreground: colors.hidden.onIllustrationDark100,
          border: colors.other.none,
        },
        disabled: {
          background: colors.other.none,
          foreground: colors.hidden.onIllustrationDark30,
          border: colors.other.none,
        },
      };
    case 'ghost-on-color-inverse':
      return {
        spinner: 'inverse',
        default: {
          background: colors.other.none,
          foreground: colors.hidden.onIllustrationLight100,
          border: colors.other.none,
        },
        hover: {
          background: colors.hidden.onIllustrationDark20,
          foreground: colors.hidden.onIllustrationLight100,
          border: colors.other.none,
        },
        active: {
          background: colors.hidden.onIllustrationDark40,
          foreground: colors.hidden.onIllustrationLight100,
          border: colors.other.none,
        },
        disabled: {
          background: colors.other.none,
          foreground: colors.hidden.onIllustrationLight40,
          border: colors.other.none,
        },
      };
    default:
      return {
        spinner: 'inverse',
        default: {
          background: colors.accent.enabled,
          foreground: colors.accent.onEnabled,
          border: colors.accent.enabled,
        },
        hover: {
          background: colors.accent.hover,
          foreground: colors.accent.onEnabled,
          border: colors.accent.hover,
        },
        active: {
          background: colors.accent.active,
          foreground: colors.accent.onEnabled,
          border: colors.accent.active,
        },
        disabled: {
          background: colors.disabled.default,
          foreground: colors.disabled.onSurfaceDefault,
          border: colors.disabled.surface,
        },
      };
  }
};

/**
 * Get non-legacy `size` value.
 *
 * `Button` supports some legacy `size` values which are deprecated but many clients likely still use.
 * If the given `size` is already one of the new values, return it as is.
 * If the given `size` is one of the legacy values, map it to the new equivalent and return that value.
 *
 * TODO: remove once all clients finished migrating to the new values or we created a codemod.
 */
export const getSizeWithLegacyValuesMapped = (
  size: ResponsiveProp<ButtonSize>
): ResponsiveProp<ButtonWithoutTextSize> => {
  const sizeLegacyToNewMapping: Record<ButtonSizeLegacy, ButtonWithoutTextSize> = {
    micro: 'small',
    regular: 'default',
    jumbo: 'large',
  };
  let buttonSize: ResponsiveProp<ButtonWithoutTextSize> = 'default';

  if (typeof size === 'string') {
    // `size` is a simple string, e.g. 'small'
    if (Object.keys(sizeLegacyToNewMapping).includes(size)) {
      // type-casting to let TS know we're certain this is a legacy size value
      const legacySize = size as ButtonSizeLegacy;
      buttonSize = sizeLegacyToNewMapping[legacySize];
    } else {
      // type-casting to let TS know we're certain this is a new size value
      const newSize = size as ButtonWithoutTextSize;
      buttonSize = newSize;
    }
  } else if (typeof size === 'object') {
    // `size` is a ResponsiveProp object, e.g. { xxsmall: 'small', small: 'default', medium: 'large' }
    const responsiveButtonSize: Record<string, ButtonWithoutTextSize> = {};

    for (const [breakpointName, sizeValue] of Object.entries(size)) {
      if (sizeValue && Object.keys(sizeLegacyToNewMapping).includes(sizeValue)) {
        // type-casting to let TS know we're certain this is a legacy size value
        const legacySize = sizeValue as ButtonSizeLegacy;
        responsiveButtonSize[breakpointName] = sizeLegacyToNewMapping[legacySize];
      } else {
        // type-casting to let TS know we're certain this is a new size value
        const newSize = sizeValue as ButtonWithoutTextSize;
        responsiveButtonSize[breakpointName] = newSize;
      }

      buttonSize = responsiveButtonSize;
    }
  }

  return buttonSize;
};

/**
 * Get size-related tokens based on the given `size` prop
 */
export const getSizeTokens = (size: ButtonSize, hasIconOnly = false, loading = false) => {
  let sizeTokens;

  if (hasIconOnly) {
    switch (size) {
      case 'small':
        sizeTokens = {
          padding: tokens.space.x2,
        };
        break;
      case 'large':
        sizeTokens = {
          padding: tokens.space.x3,
        };
        break;
      case 'xlarge':
        sizeTokens = {
          padding: tokens.space.x4,
        };
        break;
      case 'xxlarge':
        sizeTokens = {
          padding: tokens.space.x6,
        };
        break;
      default:
        sizeTokens = {
          padding: tokens.space.x3,
        };
    }
  } else {
    if (loading) {
      switch (size) {
        case 'small':
          sizeTokens = {
            paddingVertical: tokens.space.x2,
            paddingHorizontal: tokens.space.x8,
          };
          break;
        case 'large':
          sizeTokens = {
            paddingVertical: tokens.space.x3,
            paddingHorizontal: tokens.space.x12,
          };
          break;
        default:
          sizeTokens = {
            paddingVertical: tokens.space.x3,
            paddingHorizontal: tokens.space.x10,
          };
      }
    } else {
      switch (size) {
        case 'small':
          sizeTokens = {
            paddingVertical: tokens.space.x2,
            paddingHorizontal: tokens.space.x4,
            gap: tokens.space.x1,
          };
          break;
        case 'large':
          sizeTokens = {
            paddingVertical: tokens.space.x3,
            paddingHorizontal: tokens.space.x8,
            gap: tokens.space.x3,
          };
          break;
        default:
          sizeTokens = {
            paddingVertical: tokens.space.x3,
            paddingHorizontal: tokens.space.x6,
            gap: tokens.space.x2,
          };
      }
    }
  }

  return sizeTokens;
};

/**
 * Get the value for the `ShortText` `size` based on the `Button` `size`.
 *
 * The `size` values for the `Button` and the internally used `ShortText` are slightly different.
 * So we map them here and make sure that responsiveProp values are handled correctly as well.
 */
export const getFontSize = (size: ResponsiveProp<ButtonWithoutTextSize>) => {
  let fontSize: ShortTextProps['size'] = 'default';

  const mapButtonSizeToFontSize: Record<ButtonWithoutTextSize, ShortTextSize> = {
    small: 'default',
    default: 'default',
    large: 'large',
    xlarge: 'large',
    xxlarge: 'large',
  };

  if (typeof size === 'string') {
    // `size` is a simple string, e.g. 'small'
    fontSize = mapButtonSizeToFontSize[size];
  } else if (typeof size === 'object') {
    // `size` is a ResponsiveProp object, e.g. { xxsmall: 'small', small: 'default', medium: 'large' }

    const responsiveFontSize: Record<string, ShortTextSize> = {};

    for (const [breakpointName, sizeValue] of Object.entries(size)) {
      if (sizeValue && mapButtonSizeToFontSize[sizeValue]) {
        responsiveFontSize[breakpointName] = mapButtonSizeToFontSize[sizeValue];
      }

      fontSize = responsiveFontSize;
    }
  }

  return fontSize;
};

/**
 * Get the value for the `Icon` `size` based on the `Button` `size`.
 *
 * The `size` values for the `Button` and the internally used `Icon` are slightly different.
 * So we map them here and make sure that responsiveProp values are handled correctly as well.
 */
export const getIconSize = (size: ResponsiveProp<ButtonWithoutTextSize>) => {
  let iconSize: InternalIconProps['size'] = 'default';

  const mapButtonSizeToIconSize: Record<ButtonWithoutTextSize, IconSize> = {
    small: 'small',
    default: 'small',
    large: 'default',
    xlarge: 'large',
    xxlarge: 'large',
  };

  if (typeof size === 'string') {
    // `size` is a simple string, e.g. 'small'
    iconSize = mapButtonSizeToIconSize[size];
  } else if (typeof size === 'object') {
    // `size` is a ResponsiveProp object, e.g. { xxsmall: 'small', small: 'default', medium: 'large' }
    const responsiveIconSize: Record<string, IconSize> = {};

    for (const [breakpointName, sizeValue] of Object.entries(size)) {
      if (sizeValue && mapButtonSizeToIconSize[sizeValue]) {
        responsiveIconSize[breakpointName] = mapButtonSizeToIconSize[sizeValue];
      }

      iconSize = responsiveIconSize;
    }
  }

  return iconSize;
};

/**
 * Get the value for the `Spinner` `size` based on the `Button` `size`.
 *
 * The `size` values for the `Button` and the internally used `Spinner` are slightly different.
 * So we map them here and make sure that responsiveProp values are handled correctly as well.
 */
export const getSpinnerSize = (
  size: ResponsiveProp<ButtonWithoutTextSize>
): SpinnerProps['size'] => {
  let spinnerSize: SpinnerProps['size'] = 'small';

  const mapButtonSizeToSpinnerSize: Record<ButtonWithoutTextSize, SpinnerSize> = {
    small: 'small',
    default: 'small',
    large: 'medium',
    xlarge: 'large',
    xxlarge: 'large',
  };

  if (typeof size === 'string') {
    // `size` is a simple string, e.g. 'small'
    spinnerSize = mapButtonSizeToSpinnerSize[size];
  } else if (typeof size === 'object') {
    // `size` is a ResponsiveProp object, e.g. { xxsmall: 'small', small: 'default', medium: 'large' }
    const responsiveSpinnerSize: Record<string, SpinnerSize> = {};

    for (const [breakpointName, sizeValue] of Object.entries(size)) {
      if (sizeValue && mapButtonSizeToSpinnerSize[sizeValue]) {
        responsiveSpinnerSize[breakpointName] = mapButtonSizeToSpinnerSize[sizeValue];
      }

      spinnerSize = responsiveSpinnerSize;
    }
  }

  return spinnerSize;
};
